// Reac Component
import React from 'react'
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useTranslation } from 'react-i18next'

// App Component
import { FamlaServices, i18n } from '../../services'
import { Buttons, InputForm } from '../../components'
import { Routes } from '../../routes'
import { validateInvitationToken, InvitationTokenResult } from '../../utils/invitationToken'
import { handleLoginSuccess } from '../../utils/loginResponseHandler'
import { isPrivateEmail } from '../../utils/emailUtils'
import { useMicrosoftFamlaSession } from '../../hooks/useMicrosoftFamlaSession'
import {
    isStCorporateEmail,
    maybeRedirectStInviteFromPublicApp,
    shouldRedirectToStPortal,
} from '../../utils/stCorporateRedirect'
import StRedirectNotice from './StRedirectNotice'
import './UsersLogin.css'


// Features

import ButtonSocialeNetwork from './ButtonSocialeNetwork'
import { Metrics } from '../../constants'

//interface
import { iAlert } from '../../interfaces'
import { login, useAppDispatch } from '../../stores'
import iSessions from './Sessions'
import { iUser } from '../../components/type'
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../services/api/uiButtonStatsService'
import { LoginButton } from '../../auth'

const UsersSignin = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    
    // [INVITATION TOKEN] Lien direct /auth/signup?invitation_token=… (sans passer par AuthView) : compte existant → login
    React.useEffect(() => {
        const invitationToken = searchParams.get("invitation_token") || sessionStorage.getItem('invitation_token');
        const invitedEmailParam = searchParams.get('invited_email')?.trim();
        if (maybeRedirectStInviteFromPublicApp({ invitedEmail: invitedEmailParam, invitationToken })) {
            return;
        }
        if (!invitationToken) return;
        sessionStorage.setItem('invitation_token', invitationToken);
        validateInvitationToken(invitationToken)
            .then((result: InvitationTokenResult) => {
                if (!result.success || !result.data) return;
                sessionStorage.setItem('invited_process_id', result.data.process_id);
                sessionStorage.setItem('invited_user_email', result.data.user_email);
                if (maybeRedirectStInviteFromPublicApp({
                    invitedEmail: result.data.user_email,
                    invitationToken,
                })) {
                    return;
                }
                if (result.data.user_exists) {
                    window.history.replaceState({}, document.title, window.location.pathname);
                    navigate(Routes.AUTH_LOGIN, { replace: true });
                }
            })
            .catch((error: unknown) => {
                console.error('🔗 [UsersSignin] Error validating invitation token:', error);
            });
    }, [searchParams, navigate]);

    // [INVITATION] Pré-remplir l’email attendu (token process / interview)
    React.useEffect(() => {
        const invitedEmail = sessionStorage.getItem('invited_user_email');
        if (invitedEmail?.trim()) {
            setCompagny((prev) => (prev.email ? prev : { ...prev, email: invitedEmail.trim() }));
        }
    }, []);
    
    // [COMPATIBILITÉ ANCIENNE] Récupérer et stocker le process_id depuis l'URL si présent
    const invitedProcessId = searchParams.get("invited_process");
    if (invitedProcessId) {
        // Stocker dans sessionStorage pour le récupérer après vérification email
        sessionStorage.setItem('invited_process_id', invitedProcessId);
    }
    const [compagny, setCompagny] = React.useState({
        name: '',
        email: '',
        industry: '',
        password: '',
        isSubmit: false,
        isValidMail: true
    })
    const [alert, setAlert] = React.useState<iAlert>({
        isActive: false,
        type: '',
        message: ''
    })
    /** Adresse corporate à orienter vers le portail SSO (affiche l'interstitiel). */
    const [stRedirectEmail, setStRedirectEmail] = React.useState<string | null>(null)
    const { isLoading: microsoftSessionLoading } = useMicrosoftFamlaSession({
        enabled: sessionStorage.getItem('microsoft_auth_intent') === 'signup',
        redirectQueryParam: searchParams.get('redirect'),
        onStart: React.useCallback(() => {
            setAlert((currentAlert) => (
                currentAlert.isActive && currentAlert.type === 'danger'
                    ? { isActive: false, type: '', message: '' }
                    : currentAlert
            ));
        }, []),
        onError: React.useCallback(() => {
            setAlert({
                isActive: true,
                type: 'danger',
                message: t('views.auth.anErrorOcured')
            });
        }, [t]),
    });

    const redirectToLoginIfInviteAccountExists = React.useCallback(
        async (emailRaw: string): Promise<boolean> => {
            const inviteOwner = searchParams.get('invite') === '1';
            const hasInvitedProcess = Boolean(searchParams.get('invited_process')?.trim());
            if (!inviteOwner && !hasInvitedProcess) return false;
            const email = emailRaw.trim();
            if (!email || !Metrics.mailFormat.test(email) || isPrivateEmail(email)) return false;
            const data = await FamlaServices(
                'api_host',
                `/auth/invite/account-exists?email=${encodeURIComponent(email)}`,
                'GET',
                '',
                ''
            );
            if (data.status !== 200 || !data.body?.data?.account_exists) return false;
            navigate(Routes.AUTH_LOGIN, { state: { fromInviteSignup: true, email } });
            return true;
        },
        [navigate, searchParams]
    );

    // [INVITATIONS] invite=1 ou invited_process + invited_email dans le lien → vérifier compte au chargement (owner, teammate, collab legacy)
    React.useEffect(() => {
        if (searchParams.get('invitation_token')) return;
        const inviteOwner = searchParams.get('invite') === '1';
        const hasInvitedProcess = Boolean(searchParams.get('invited_process')?.trim());
        if (!inviteOwner && !hasInvitedProcess) return;
        const invitedEmailParam = searchParams.get('invited_email')?.trim();
        if (
            !invitedEmailParam ||
            isPrivateEmail(invitedEmailParam) ||
            !Metrics.mailFormat.test(invitedEmailParam)
        ) {
            return;
        }
        let cancelled = false;
        (async () => {
            const data = await FamlaServices(
                'api_host',
                `/auth/invite/account-exists?email=${encodeURIComponent(invitedEmailParam)}`,
                'GET',
                '',
                ''
            );
            if (cancelled) return;
            if (data.status === 200 && data.body?.data?.account_exists) {
                navigate(Routes.AUTH_LOGIN, {
                    state: { fromInviteSignup: true, email: invitedEmailParam },
                });
                return;
            }
            setCompagny((prev) => (prev.email ? prev : { ...prev, email: invitedEmailParam }));
        })();
        return () => {
            cancelled = true;
        };
    }, [searchParams, navigate]);

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.auth.signupSubmit);

        if (compagny.email.length === 0) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.emailIsRequired') });
            return;
        }
        else if (compagny.name.length === 0) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.compagnyNameIsRequired') });
            return;
        }

        // Vérifier si l'email est professionnel avant de soumettre
        if (isPrivateEmail(compagny.email)) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.professionalEmail') });
            setCompagny({ ...compagny, isSubmit: false });
            return;
        }

        // [ST REDIRECT] Saisie corporate sur l'instance publique → interstitiel puis portail SSO.
        // L'écran envoie le lien par email avant de naviguer ; aucun compte n'est créé ici.
        if (shouldRedirectToStPortal() && isStCorporateEmail(compagny.email)) {
            setStRedirectEmail(compagny.email.trim());
            setCompagny({ ...compagny, isSubmit: false });
            return;
        }

        if (await redirectToLoginIfInviteAccountExists(compagny.email)) {
            return;
        }

        // Détecter la langue actuelle de l'application
        // Priorité: i18n.language > localStorage > navigator.language
        let currentLang = i18n.language;
        if (!currentLang || currentLang === 'undefined') {
            // Si i18n n'est pas encore initialisé, utiliser localStorage
            const savedLang = localStorage.getItem('currentLanguage');
            if (savedLang) {
                currentLang = savedLang;
            } else {
                // Sinon, détecter depuis le navigateur
                const browserLang = navigator.language || navigator.languages?.[0] || 'en';
                currentLang = browserLang.toLowerCase().startsWith('fr') ? 'fr' : 'en';
            }
        }
        // Normaliser la langue : convertir 'fr-FR', 'fr-CA', etc. en 'fr'
        const normalizedLang = currentLang?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
        const backendLang = normalizedLang === 'fr' ? 'french' : 'english';
        
        let formData = {
            fullname: compagny.name,
            email: compagny.email,
            password: compagny.password,
            passwordConfirm: compagny.password,
            lang: backendLang
        }
        setCompagny({ ...compagny, isSubmit: true });
        let data = await FamlaServices("api_host", "/auth/register", "POST", formData, "")

        // Filet de sécurité : l'API refuse les adresses corporate sur l'instance publique.
        if (data.status === 403 && data.body?.data?.sso_portal_url) {
            setCompagny({ ...compagny, isSubmit: false });
            setStRedirectEmail(compagny.email.trim());
            return;
        }

        if (data.status === 201) {
            const payload = data.body?.data;
            const accessToken = payload?.access_token;

            if (accessToken && payload?.session) {
                const sessions: iSessions = payload.session;
                dispatch(login({
                    token: accessToken,
                    session: sessions,
                    user: sessions.user,
                }));
                handleLoginSuccess(data.body);
                setCompagny({ ...compagny, name: '', email: '', password: '', isSubmit: false, isValidMail: true });

                const invitedProcessId = sessionStorage.getItem('invited_process_id');
                if (sessions.company_id === null || sessions.company_id === undefined || sessions.company_id === '') {
                    navigate(Routes.CREATE_MY_COMPANY);
                } else if (invitedProcessId) {
                    navigate(`${Routes.PROCESS_INTERVIEW}${invitedProcessId}`, { replace: true });
                } else {
                    navigate(Routes.HOMEVIEW);
                }
                return;
            }

            let session = {} as iSessions;
            let user: iUser = payload as iUser;
            dispatch(login({
                token: "",
                session: session,
                user: user
            }));

            setCompagny({ ...compagny, name: '', email: '', password: '', isSubmit: false, isValidMail: true });
            navigate(Routes.AUTH_MAIL);
        }
        else if (data.body.status_code === 409) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.accountAlreadyExist') });
            setCompagny({ ...compagny, password: '', isSubmit: false });
        }
        else if (data.body.status_code === 400 && data.body.description === "This email address has been banned from Famla") {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.thisEmailHasBanned') });
            setCompagny({ ...compagny, password: '', isSubmit: false });
        }
        else if (data.body.status_code === 400 && data.body.description === "Passwords do not match") {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.passwordsNotMatch') });
            setCompagny({ ...compagny, password: '', isSubmit: false });
        }
        else if (data.body.status_code === 400 && (data.body.description === "dont use private" || data.body.description?.includes("professional email") || data.body.description?.includes("Personal email"))) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.professionalEmail') });
            setCompagny({ ...compagny, password: '', isSubmit: false });
        }
        else {
            setCompagny({ ...compagny, password: '', isSubmit: false });
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.anErrorOcured') });
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { type, name, value, checked } = event.target;
        let fieldValue: string | number | boolean;
        if (type === 'checkbox') {
            fieldValue = checked;
        } else if (type === 'number') {
            fieldValue = value !== '' ? Number(value) : '';
        } else {
            fieldValue = value !== undefined ? value : '';
        }
        fieldValue = typeof fieldValue !== 'string' ? String(fieldValue) : fieldValue;
        const trimmedValue: string = typeof fieldValue === 'string' ? fieldValue.trim() : '';
        let isEmailValid = name === 'email' ? Metrics.mailFormat.test(trimmedValue) : true;

        setCompagny({ ...compagny, [name]: fieldValue, isValidMail: isEmailValid });
    };

    return (
        <div className="users-login-light">
            {stRedirectEmail && (
                <StRedirectNotice
                    email={stRedirectEmail}
                    onCancel={() => setStRedirectEmail(null)}
                />
            )}
            <div className="users-login-title-block">
                <h1 className="users-login-headline">{t('views.auth.signUp')}</h1>
            </div>
            <form >
                {alert.isActive &&
                    <div className={`alert alert-${alert.type}`} role="alert">
                        {alert.message}
                    </div>
                }

                <LoginButton
                    label={t('views.auth.signUpWithMicrosoft') || 'Sign Up with Microsoft'}
                    loadingLabel={t('views.auth.authenticating') || t('views.auth.pleaseWait') || 'Connexion en cours...'}
                    className="btn auth-ms-login-btn w-100 d-flex align-items-center justify-content-center"
                    spinnerClass="text-secondary"
                    isProcessing={microsoftSessionLoading}
                    redirectPath="/auth/signup"
                    authIntent="signup"
                    trackEvent={UI_BUTTON_EVENTS.auth.signupMicrosoft}
                />
                <div className="mt-3">
                    <ButtonSocialeNetwork t={t} />
                </div>

                <div className="users-login-divider d-flex align-items-center">
                    <hr className="flex-grow-1" />
                    <span>{t('views.auth.or')}</span>
                    <hr className="flex-grow-1" />
                </div>

                <InputForm
                    inputType="email"
                    inputLabel={t('views.auth.emailAddresPro')}
                    inputName="email"
                    inputValue={compagny.email}
                    isFloatingLabel={true}
                    labelClass="text-theme"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                    inputOnBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        void redirectToLoginIfInviteAccountExists(e.target.value);
                    }}
                    inputContainerClass={compagny.isValidMail ? 'mb-3' : 'mb-0'}
                />
                {!(compagny.isValidMail) && <span className={`text-danger fs-6 d-bloc ${!compagny.isValidMail ? 'mb-3' : 'mb-0'}`}>{compagny.isValidMail ? '' : t('views.auth.enterValidMail')}</span>}

                <InputForm
                    inputType="text"
                    inputLabel={t('words.name')}
                    inputName="name"
                    inputValue={compagny.name}
                    isFloatingLabel={true}
                    labelClass="text-theme"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                    inputContainerClass={compagny.isValidMail ? 'mb-3' : 'mb-0'}
                />
                {/* <SelectForm
                    formClass="mb-3"
                <SelectForm
                    selectClass="mb-3"
                    placeholder="Select Industry"
                    selectName={compagny.industry}
                    selectOptions={selectOptions}
                    onChange={(e) => handleChange(e)}
                /> */}
                <InputForm
                    inputType="password"
                    inputLabel={t('views.auth.password')}
                    inputName="password"
                    inputValue={compagny.password}
                    isFloatingLabel={true}
                    labelClass="text-theme"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                    inputContainerClass={compagny.isValidMail ? 'mb-3 ' : 'mt-2'}
                    withIcon={true}
                    iconClass="text-theme"
                />

                <Buttons
                    buttonType="submit"
                    name={t('views.auth.getStartedForFree') || 'Get started for free'}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleSubmit(event)}
                    isLoading={compagny.isSubmit}
                    btnClass="users-login-submit w-100"
                    labelClass="text-white fw-bold"
                    styleProps={{ minHeight: 48 }}
                    arrowVisible={false}
                    spinnerColor="text-white"
                    arrowColor=""
                    disabled={compagny.password.length === 0 || compagny.email.length === 0 || compagny.name.length === 0}
                />
            </form>

            <p className="users-login-signup-footer mb-0">
                {t('views.auth.alreadyHaveAccount')}{' '}
                <Link
                    to={Routes.AUTH_LOGIN}
                    className="users-login-terracotta-link fw-medium"
                    onClick={async (e) => {
                        e.preventDefault();
                        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.auth.signupLoginLink);
                        navigate(Routes.AUTH_LOGIN);
                    }}
                >
                    {t('views.auth.logIn')}
                </Link>
            </p>
            <p className="users-login-referral-footer mb-0">
                <Link
                    to={Routes.AUTH_REFERRAL}
                    className="users-login-terracotta-link fw-medium"
                    onClick={async (e) => {
                        e.preventDefault();
                        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.auth.signupReferralLink);
                        navigate(Routes.AUTH_REFERRAL);
                    }}
                >
                    {t('views.auth.signUpWithReferral')}
                </Link>
            </p>
        </div>
    )
}

export default UsersSignin;